import type { Rect } from '../../shared/types';
import { clamp, mix } from './paint';

interface Body extends Rect { id: string; connected?: boolean; entered?: boolean; grounded?: boolean }
type Side = 'left' | 'right' | 'above' | 'below';
const SIDES: Side[] = ['left', 'right', 'above', 'below'];
const EPS = 1e-7;
const axis = (side: Side) => side === 'left' || side === 'right' ? 'x' : 'y';
const sign = (side: Side) => side === 'left' || side === 'above' ? -1 : 1;

function gap(a: Rect, b: Rect, side: Side): number {
  switch (side) {
    case 'left': return b.x - a.x - a.w;
    case 'right': return a.x - b.x - b.w;
    case 'above': return b.y - a.y - a.h;
    case 'below': return a.y - b.y - b.h;
  }
}

/** Interpolate one received interval without taking a chord through a body's corner.
 *
 * A separating half-plane shared by both endpoints is already safe under linear
 * interpolation. When separation changes axes, retain the initial contact until the
 * orthogonal axis clears, then continue to the exact received endpoint. This is a
 * continuous, piecewise-linear display path, not collision simulation or extrapolation.
 */
export function sampleBodyInterval<T extends Body>(before: T[], after: T[], amount: number): {
  bodies: T[]; cornerTransitions: number; maxCorrection: number;
} {
  const t = clamp(amount, 0, 1);
  const previous = new Map(before.map(body => [body.id, body]));
  const starts = after.map(body => previous.get(body.id) ?? body);
  const bodies = after.map((body, index) => {
    const start = starts[index];
    if (Math.hypot(body.x - start.x, body.y - start.y) > 170) return { ...body };
    return { ...body, x: mix(start.x, body.x, t), y: mix(start.y, body.y, t) };
  });
  const raw = bodies.map(body => ({ x: body.x, y: body.y }));
  const enabled = (i: number) => starts[i].connected !== false && after[i].connected !== false &&
    !starts[i].entered && !after[i].entered && Math.hypot(after[i].x - starts[i].x, after[i].y - starts[i].y) <= 170;

  // Persisted support contacts move rigidly with a corrected member. This preserves
  // the rail/feet relation for a carried group that itself rounds another body.
  const parent = after.map((_, i) => i);
  const root = (i: number): number => parent[i] === i ? i : (parent[i] = root(parent[i]));
  for (let i = 0; i < after.length; i++) for (let j = i + 1; j < after.length; j++) {
    if (!enabled(i) || !enabled(j)) continue;
    const overlapX = (a: Rect, b: Rect) => Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x) > EPS;
    if (!overlapX(starts[i], starts[j]) || !overlapX(after[i], after[j])) continue;
    if ((['above', 'below'] as const).some(side => Math.abs(gap(starts[i], starts[j], side)) <= EPS && Math.abs(gap(after[i], after[j], side)) <= EPS)) {
      parent[root(j)] = root(i);
    }
  }

  let cornerTransitions = 0;
  for (let i = 0; i < after.length; i++) for (let j = i + 1; j < after.length; j++) {
    if (!enabled(i) || !enabled(j) || root(i) === root(j)) continue;
    const g0 = SIDES.map(side => gap(starts[i], starts[j], side));
    const g1 = SIDES.map(side => gap(after[i], after[j], side));
    if (g0.some((value, index) => value >= -EPS && g1[index] >= -EPS)) continue;

    let leave = 0, clear = 1;
    let from: Side | null = null, to: Side | null = null;
    for (let k = 0; k < SIDES.length; k++) {
      if (g0[k] >= -EPS && g1[k] < -EPS) {
        const at = clamp(g0[k] / (g0[k] - g1[k]), 0, 1);
        if (from === null || at > leave) { leave = at; from = SIDES[k]; }
      }
      if (g0[k] < -EPS && g1[k] >= -EPS) {
        const at = clamp(-g0[k] / (g1[k] - g0[k]), 0, 1);
        if (to === null || at < clear) { clear = at; to = SIDES[k]; }
      }
    }
    if (!from || !to || axis(from) === axis(to) || leave >= clear - EPS) continue;
    cornerTransitions++;

    const fromIndex = SIDES.indexOf(from);
    const travelAxis = axis(to);
    let moveI = Math.abs(after[i][travelAxis] - starts[i][travelAxis]) >= Math.abs(after[j][travelAxis] - starts[j][travelAxis]);
    if (axis(from) === 'y') {
      const fixedI = starts[i].grounded && after[i].grounded && Math.abs(after[i].y - starts[i].y) < EPS;
      const fixedJ = starts[j].grounded && after[j].grounded && Math.abs(after[j].y - starts[j].y) < EPS;
      if (fixedI !== fixedJ) moveI = !fixedI;
    }
    const moveRoot = root(moveI ? i : j);
    const adjust = (side: Side, desiredGap: number) => {
      const delta = desiredGap - gap(bodies[i], bodies[j], side);
      const shift = delta * sign(side) * (moveI ? 1 : -1);
      for (let k = 0; k < bodies.length; k++) if (root(k) === moveRoot) bodies[k][axis(side)] += shift;
    };

    if (clear < 0.8) {
      // Usual side-entry / walking-off-a-head case: retain the untouched travel axis.
      const desired = t <= leave ? mix(g0[fromIndex], g1[fromIndex], t)
        : t <= clear ? 0 : mix(0, g1[fromIndex], (t - clear) / (1 - clear));
      adjust(from, desired);
    } else {
      // An endpoint that only just touches the new face has no time left for the
      // other axis. Pass through the exact corner continuously, rather than holding
      // a coordinate until t=1 and snapping to the authoritative endpoint.
      const corner = clamp((leave + clear) / 2, 0.15, 0.85);
      for (const side of [from, to]) {
        const k = SIDES.indexOf(side);
        const desired = t <= corner ? mix(g0[k], 0, t / corner) : mix(0, g1[k], (t - corner) / (1 - corner));
        adjust(side, desired);
      }
    }
  }
  return { bodies, cornerTransitions, maxCorrection: Math.max(0, ...bodies.map((body, index) => Math.hypot(body.x - raw[index].x, body.y - raw[index].y))) };
}
